import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import JSZip from "jszip";
import axios from "../../configApi/axiosConfig";
import { APIURL } from "../../configApi/apiConfig";
import { useNotificationContext } from "../AuthContext/NotificationContext";
import { FiltrosCarteraBS } from "./FiltrosCarteraBS";
import CarteraTableRow from "./CarteraTableRow";
import SolidarioCheckbox from "./SolidarioCheckbox";

// Primer banco del futuro selector multi-banco de generación de cartera. Los
// demás quedan listados pero deshabilitados hasta que se integren sus propios
// endpoints de generación — no se crea un módulo aparte para esto todavía,
// para no sobre-construir antes de tener un segundo banco real.
const BANCOS = [
  { value: "solidario", label: "Banco Solidario", disabled: false },
  { value: "pichincha", label: "Banco Pichincha (próximamente)", disabled: true },
  { value: "guayaquil", label: "Banco Guayaquil (próximamente)", disabled: true },
];

const hoyISO = () => new Date().toISOString().split("T")[0];
const PLACEHOLDER_ROWS = [{ opacity: "opacity-40" }, { opacity: "opacity-20" }];

const Spinner = ({ className = "h-5 w-5 text-white" }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export function CarteraBancoSolidario() {
  const { addNotification } = useNotificationContext();

  const [banco, setBanco] = useState("solidario");

  const [fechaInicio, setFechaInicio] = useState(hoyISO());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [filtro, setFiltro] = useState("");
  const [lote, setLote] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    fechaInicio: hoyISO(),
    fechaFin: hoyISO(),
    filtro: "",
    lote: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  const [data, setData] = useState([]);
  const [numeroUltimoLote, setNumeroUltimoLote] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSftpOpen, setConfirmSftpOpen] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [enviandoSftp, setEnviandoSftp] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Quitar del lote es una acción por fila (no masiva): solo tiene sentido para una
  // solicitud que YA tiene un N. Lote asignado, y se confirma/opera de a una.
  const [idParaQuitar, setIdParaQuitar] = useState(null);
  const [quitandoId, setQuitandoId] = useState(null);

  const fetchSolicitudes = useCallback(async (page, limit, filters) => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const response = await axios.get(
        APIURL.getCarteraBSSolicitudes({ limit, offset, ...filters }),
      );
      setData(response.data?.data || []);
      setTotal(response.data?.total || 0);
      setNumeroUltimoLote(response.data?.numeroUltimoLote || 0);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo cargar el listado",
        subtitle: "Intenta nuevamente en unos segundos.",
      });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, appliedFilters]);

  const handleConsultar = () => {
    setCurrentPage(1);
    setAppliedFilters({ fechaInicio, fechaFin, filtro, lote });
  };

  const changePage = (page) => setCurrentPage(page);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const currentPageIds = useMemo(() => data.map((d) => d.idCre_SolicitudWeb), [data]);
  const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
  const someCurrentSelected = currentPageIds.some((id) => selectedIds.has(id)) && !allCurrentSelected;
  const selectedRows = useMemo(
    () => data.filter((row) => selectedIds.has(row.idCre_SolicitudWeb)),
    [data, selectedIds],
  );
  const hasConfirmedSelected = selectedRows.some((row) => row.confirmado === true);
  const confirmedSelectionOnly = selectedIds.size > 0
    && selectedRows.length === selectedIds.size
    && selectedRows.every((row) => row.confirmado === true);

  const toggleAllCurrentPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const descargarZipCartera = async (resultado) => {
    const archivos = [resultado?.clientes, resultado?.creditos, resultado?.cuotas]
      .filter((r) => r?.ok && r?.content && r?.fileName);

    if (archivos.length === 0) return;

    try {
      const zip = new JSZip();
      archivos.forEach((archivo) => zip.file(archivo.fileName, archivo.content));

      const blob = await zip.generateAsync({ type: "blob" });
      const downloadUrl = window.URL.createObjectURL(blob);

      const now = new Date();
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const loteSuffix = resultado?.creditos?.numeroLote ? `_Lote${resultado.creditos.numeroLote}` : "";
      const zipFileName = `cartera_banco_solidario_${dd}${mm}${loteSuffix}.zip`;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo generar el archivo .zip de descarga",
        subtitle: "Los datos ya se generaron correctamente en el banco; intenta descargarlos manualmente si es necesario.",
      });
    }
  };

  const handleGenerarCartera = async () => {
    setConfirmOpen(false);
    setGenerando(true);
    try {
      const response = await axios.post(APIURL.postGenerarCarteraBS(), {
        idsCre_SolicitudWeb: Array.from(selectedIds),
      });
      const resultado = response.data;
      const pasos = [
        ...(!resultado?.regeneracion ? [{
          nombre: "Reserva de ofertas",
          resultado: {
            ok: Array.isArray(resultado?.reservas) && resultado.reservas.every((reserva) => reserva.ok),
            message: resultado?.reservas
              ?.filter((reserva) => !reserva.ok)
              .map((reserva) => `${reserva.idCre_SolicitudWeb}: ${reserva.message}`)
              .join(" | "),
          },
        }] : []),
        { nombre: "Clientes", resultado: resultado?.clientes },
        { nombre: "Créditos", resultado: resultado?.creditos },
        { nombre: "Cuotas", resultado: resultado?.cuotas },
      ];
      const fallidos = pasos.filter((p) => !p.resultado?.ok);

      if (fallidos.length === 0) {
        addNotification({
          type: "success",
            message: resultado?.regeneracion ? "Lote regenerado correctamente" : "Lote generado correctamente",
          subtitle: resultado?.creditos?.numeroLote
            ? `Lote N.º ${resultado.creditos.numeroLote}`
            : undefined,
        });
      } else {
        const mensajesFallidos = fallidos.map((p) => ({
          nombre: p.nombre,
          mensaje: p.resultado?.message || "no se pudo generar",
        }));
        const mensajesUnicos = [
          ...new Set(mensajesFallidos.map(({ mensaje }) => mensaje)),
        ];
        const subtitle = mensajesUnicos
          .map((mensaje) => {
            const pasosConMismoMensaje = mensajesFallidos.filter(
              (item) => item.mensaje === mensaje
            );
            return pasosConMismoMensaje.length === fallidos.length
              ? mensaje
              : `${pasosConMismoMensaje.map((item) => item.nombre).join(", ")}: ${mensaje}`;
          })
          .join(" | ");

        addNotification({
          type: "error",
          message: "El lote se generó con errores",
          subtitle,
        });
      }

      fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
      await descargarZipCartera(resultado);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo generar el lote",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setGenerando(false);
    }
  };

  const descargarExcelBase64 = (base64, fileName) => {
    if (!base64) return;
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName || "documentos_banco_solidario.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleEnviarDocumentosSftp = async () => {
    setConfirmSftpOpen(false);
    setEnviandoSftp(true);
    try {
      const response = await axios.post(APIURL.postEnviarDocumentosSftpBS(), {
        idsCre_SolicitudWeb: Array.from(selectedIds),
      }, { timeout: 300_000 });
      const resultado = response.data;

      descargarExcelBase64(resultado?.excelBase64, resultado?.excelFileName);

      const enviados = (resultado?.resultados || []).filter((item) => item.ok).length;
      const fallidos = (resultado?.resultados || []).filter((item) => !item.ok).length;
      const omitidos = resultado?.skipped?.length || 0;

      if (resultado?.ok) {
        addNotification({
          type: "success",
          message: "Documentos enviados al SFTP",
          subtitle: `${enviados} solicitud${enviados !== 1 ? "es" : ""} procesada${enviados !== 1 ? "s" : ""}. El Excel se descargó automáticamente.`,
        });
        setSelectedIds(new Set());
      } else {
        addNotification({
          type: "error",
          message: "El envío al SFTP terminó con novedades",
          subtitle: resultado?.message || `${enviados} enviadas, ${fallidos} con error y ${omitidos} sin lote.`,
        });
      }
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudieron enviar los documentos al SFTP",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setEnviandoSftp(false);
    }
  };

  const handleQuitarDelLote = async () => {
    const id = idParaQuitar;
    setIdParaQuitar(null);
    setQuitandoId(id);
    try {
      const response = await axios.post(APIURL.postDesgenerarCarteraBS(), {
        idsCre_SolicitudWeb: [id],
      });
      const resultado = response.data;
      const skipped = resultado?.skipped || [];

      if (skipped.length === 0) {
        addNotification({
          type: "success",
          message: "Solicitud quitada del lote",
          subtitle: "Vuelve a quedar pendiente para un próximo lote de Banco Solidario.",
        });
      } else {
        addNotification({
          type: "error",
          message: "No se pudo quitar del lote",
          subtitle: skipped[0]?.reason,
        });
      }

      fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo quitar del lote",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setQuitandoId(null);
    }
  };

  const handleExportarExcel = async () => {
    setExportando(true);
    try {
      const response = await axios.get(APIURL.getCarteraBSSolicitudesExcel(appliedFilters), {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "cartera_banco_solidario.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      addNotification({ type: "success", message: "Excel generado", subtitle: "La descarga debería iniciar automáticamente." });
    } catch (error) {
      addNotification({ type: "error", message: "No se pudo generar el Excel", subtitle: "Intenta nuevamente en unos segundos." });
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="p-8 bg-white min-h-screen">
      {/* Encabezado */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <img
            src="/img/Solidario+.jpg"
            alt="Banco Solidario"
            className="w-12 h-12 rounded-lg object-cover shadow-sm"
          />
          <h1 className="text-2xl font-bold text-[#01BEDE]">Banco Solidario - Cartera de Créditos</h1>
        </div>
        <select
          value={banco}
          onChange={(e) => setBanco(e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 text-gray-600 focus:ring-2 focus:ring-[#01BEDE] focus:border-[#01BEDE] outline-none"
        >
          {BANCOS.map((b) => (
            <option key={b.value} value={b.value} disabled={b.disabled}>
              {b.label}
            </option>
          ))}
        </select>
      </div>

      {/* Filtros */}
      <FiltrosCarteraBS
        fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
        fechaFin={fechaFin} setFechaFin={setFechaFin}
        filtro={filtro} setFiltro={setFiltro}
        lote={lote} setLote={setLote}
        numeroUltimoLote={numeroUltimoLote}
        handleConsultar={handleConsultar} loadingConsulta={loading}
      />

      {/* Barra de acción */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-4 text-sm font-bold text-gray-700">
          <span>{total} REGISTRO{total !== 1 ? "S" : ""} - {selectedIds.size} SELECCIONADO{selectedIds.size !== 1 ? "S" : ""}</span>
          <span className="text-gray-300">|</span>
          <span>ESTADO: PENDIENTE / CONFIRMADO</span>
        </div>
        <div className="flex gap-3">
          <button
            className="bg-[#01BEDE] text-white px-6 py-2 rounded-lg shadow-lg shadow-[#01BEDE]/30 font-semibold hover:bg-[#019CB8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => setConfirmOpen(true)}
            disabled={selectedIds.size === 0 || generando || enviandoSftp}
          >
            {generando && <Spinner className="h-4 w-4 text-white" />}
            {generando ? "Generando..." : "Generar Lote"}
          </button>
          <button
            className="bg-[#0F766E] text-white px-6 py-2 rounded-lg shadow-lg shadow-[#0F766E]/30 font-semibold hover:bg-[#0B5F59] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => setConfirmSftpOpen(true)}
            disabled={selectedIds.size === 0 || hasConfirmedSelected || enviandoSftp || generando}
            title={hasConfirmedSelected ? "Las compras confirmadas no pueden volver a enviarse al SFTP" : undefined}
          >
            {enviandoSftp && <Spinner className="h-4 w-4 text-white" />}
            {enviandoSftp ? "Enviando..." : "Enviar documentos SFTP"}
          </button>
          <button
            className="border-2 border-[#01BEDE] text-[#01BEDE] px-6 py-2 rounded-lg font-semibold hover:bg-[#01BEDE]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleExportarExcel}
            disabled={exportando || total === 0}
          >
            {exportando && <Spinner className="h-4 w-4 text-[#01BEDE]" />}
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left">
                <SolidarioCheckbox
                  checked={allCurrentSelected}
                  indeterminate={someCurrentSelected}
                  onChange={toggleAllCurrentPage}
                />
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Lote</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">N. Solicitud</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Fecha</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Cédula</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Nombre del Cliente</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">N. Operación</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {!loading && data.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  No hay solicitudes de Banco Solidario para mostrar.
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <CarteraTableRow
                  key={row.idCre_SolicitudWeb}
                  data={row}
                  index={index}
                  checked={selectedIds.has(row.idCre_SolicitudWeb)}
                  onToggle={toggleRow}
                  onQuitarLote={setIdParaQuitar}
                  quitando={quitandoId === row.idCre_SolicitudWeb}
                />
              ))
            )}
            {/* Filas de relleno: refuerzan el estado de carga con un fundido, igual
                que el mockup — no son shimmer animado, son un desvanecido estático. */}
            {loading && PLACEHOLDER_ROWS.map((p, i) => (
              <tr key={`placeholder-${i}`} className={p.opacity}>
                <td className="px-4 py-4"><div className="h-4 w-4 bg-gray-200 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-8 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-20 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-48 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-20 bg-gray-100 rounded" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <div className="hidden md:flex justify-between items-center">
            <div className="w-1/3" />
            <div className="flex justify-center items-center gap-4 w-1/3">
              <button
                onClick={() => changePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ‹
              </button>
              <span className="font-semibold text-gray-600">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => changePage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ›
              </button>
            </div>
            <div className="w-1/3 flex justify-end">
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {[10, 20, 30, 50].map((value) => (
                  <option key={value} value={value}>{value} por página</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex md:hidden flex-col gap-4 items-center">
            <div className="w-full flex justify-center">
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {[10, 20, 30, 50].map((value) => (
                  <option key={value} value={value}>{value} por página</option>
                ))}
              </select>
            </div>
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => changePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ‹
              </button>
              <span className="font-semibold text-gray-600">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => changePage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar generación de lote</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmedSelectionOnly ? (
              <>Se regenerarán los archivos del lote existente para <strong>{selectedIds.size}</strong> solicitud
                {selectedIds.size !== 1 ? "es" : ""} confirmada{selectedIds.size !== 1 ? "s" : ""}.</>
            ) : (
              <>Las <strong>{selectedIds.size}</strong> solicitud{selectedIds.size !== 1 ? "es" : ""} seleccionada
                {selectedIds.size !== 1 ? "s" : ""} son las elegidas para ser compradas por Banco Solidario.</>
            )}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {confirmedSelectionOnly
              ? "Solo se regenerarán Clientes, Créditos y Cuotas con el mismo número de lote. No se reservará nuevamente la oferta ni se modificará la compra."
              : "Al confirmar se reservará cada oferta en Banco Solidario y, únicamente si todas son aceptadas, se generarán los archivos de Clientes, Créditos y Cuotas del lote."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleGenerarCartera} variant="contained" sx={{ backgroundColor: "#01BEDE", "&:hover": { backgroundColor: "#019CB8" } }}>
            {confirmedSelectionOnly ? "Regenerar archivos" : "Confirmar y generar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSftpOpen} onClose={() => setConfirmSftpOpen(false)}>
        <DialogTitle>Confirmar envío de documentos</DialogTitle>
        <DialogContent>
          <Typography>
            Se enviarán al SFTP los documentos de <strong>{selectedIds.size}</strong> solicitud
            {selectedIds.size !== 1 ? "es" : ""}. Al terminar se descargará el Excel del envío.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Las solicitudes que todavía estén en lote 0 se enviarán al futuro lote N.º{" "}
            <strong>{numeroUltimoLote + 1}</strong>. Las solicitudes que ya tengan un lote asignado conservarán el suyo.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSftpOpen(false)}>Cancelar</Button>
          <Button onClick={handleEnviarDocumentosSftp} variant="contained" sx={{ backgroundColor: "#0F766E", "&:hover": { backgroundColor: "#0B5F59" } }}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={idParaQuitar !== null} onClose={() => setIdParaQuitar(null)}>
        <DialogTitle>Quitar del lote de Banco Solidario</DialogTitle>
        <DialogContent>
          <Typography>
            Se quitará esta solicitud de su lote (vuelve a quedar pendiente). Solo aplica si el banco todavía no confirmó la compra.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdParaQuitar(null)}>Cancelar</Button>
          <Button onClick={handleQuitarDelLote} variant="contained" color="error">
            Quitar del Lote
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
